// ---------------------------------------------------------------------------
// CustomTypeStep — Tariff type selection (fixed / spot / tou)
// ---------------------------------------------------------------------------
// Rendered when the user chose "My tariff isn't listed" at retailer_tariff.
// Echoes metadata.template_ids back as candidate_template_ids in payload.
// ---------------------------------------------------------------------------

import { memo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { OptionCard } from '@/components/shared/OptionCard';
import { StepHeading } from '@/components/shared/StepHeading';
import { t } from '@/i18n';
import { getStepIcon } from '@/utils/stepIcons';
import { customTypeLabel } from '@/utils/stepOptions';
import { useCollectedData } from '@/hooks/useCollectedData';
import type { StepProps } from '@/types/steps';
import styles from './CustomTypeStep.module.css';

const TYPE_ICONS: Record<string, ReactNode> = {
  fixed: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="19" y2="12" />
      <polyline points="15 8 19 12 15 16" />
    </svg>
  ),
  spot: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  ),
  tou: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
};

export const CustomTypeStep = memo(function CustomTypeStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const collectedData = useCollectedData();
  const previousType = (() => {
    const ret = collectedData['retailer'];
    if (typeof ret === 'object' && ret !== null) {
      const ct = (ret as Record<string, unknown>)['custom_tariff'];
      if (typeof ct === 'object' && ct !== null) {
        const tp = (ct as Record<string, unknown>)['type'];
        return typeof tp === 'string' ? tp : '';
      }
    }
    return '';
  })();
  const handleSelect = useCallback(
    (id: string) => {
      if (isSubmitting) return;
      const opt = options.options.find((o) => o.id === id);
      const meta = opt?.metadata;
      const templateIds = Array.isArray(meta?.['template_ids'])
        ? (meta['template_ids'] as string[])
        : [];
      onSubmit({ custom_type: id, candidate_template_ids: templateIds });
    },
    [options.options, onSubmit, isSubmitting],
  );

  return (
    <>
      <StepHeading
        title={t('steps.retailer_custom_type.title')}
        subtitle={t('steps.retailer_custom_type.subtitle')}
        icon={getStepIcon(stepId)}
      />
      <div
        className={styles['list']}
        role="listbox"
        aria-label={t('steps.retailer_custom_type.title')}
      >
        {options.options.map((o) => (
          <OptionCard
            key={o.id}
            id={o.id}
            name={customTypeLabel(o.id, o.name)}
            description={o.description || undefined}
            icon={TYPE_ICONS[o.id]}
            isSelected={o.id === previousType}
            onClick={handleSelect}
          />
        ))}
      </div>
    </>
  );
});
